import React from 'react'
import { StationProps } from '../../../type/StationProps'
import { ResultWrapper } from '../../block/ResultWrapper/ResultWrapper'
import { Distance } from '../../primitive/Distance/Distance'
import { formatDataUpdatedAt } from '../../../utils/formatDataUpdatedAt'
import styles from './SubjectDetail.module.scss'

export const subjectWebsite = (value?: string) => {
    if (!value?.trim()) return undefined
    try {
        const url = new URL(
            /^[a-z][a-z\d+.-]*:/i.test(value.trim())
                ? value.trim()
                : `https://${value.trim()}`
        )
        return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined
    } catch {
        return undefined
    }
}

export const SubjectDetail = ({
    station,
    onClose,
}: {
    station: StationProps
    onClose: () => void
}) => {
    const subject = station.properties.subject
    if (!subject) return null
    const website = subjectWebsite(subject.website)
    const updatedAt = formatDataUpdatedAt(subject.updatedAt)
    return (
        <ResultWrapper
            open={true}
            openAnimated={true}
            setOpen={onClose}
            backLabel="Zpět na výsledky"
            variant="subject"
        >
            <>
                <div className={styles.header}>
                    <div className={styles.headerRow}>
                        <div className={styles.headerCol}>
                            <img
                                className={styles.headerFeatureIcon}
                                src="/img/layout/circular.svg"
                                alt=""
                            />
                        </div>
                        <div className={styles.headerCol}>
                            <h1 className={styles.headerTitle}>
                                {subject.name}
                            </h1>
                            <p className={styles.headerType}>
                                {[
                                    subject.activity?.name,
                                    subject.specialization?.name,
                                ]
                                    .filter(Boolean)
                                    .join(' – ')}
                            </p>
                        </div>
                    </div>
                    <div className={styles.headerRow}>
                        <div className={styles.headerCol}>
                            <img src="/img/icon/heart.svg" alt="" />
                        </div>
                        <div className={styles.headerCol}>
                            <button
                                type="button"
                                className={styles.rating}
                                disabled
                                title="Hodnocení zatím není dostupné"
                            >
                                <span className={styles.rateTitle}>
                                    Ohodnotit
                                </span>
                                <span
                                    className={styles.stars}
                                    aria-hidden="true"
                                >
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <img
                                            key={star}
                                            src="/img/layout/circular-star.svg"
                                            alt=""
                                        />
                                    ))}
                                </span>
                            </button>
                        </div>
                    </div>
                    <div className={styles.headerRow}>
                        <div className={styles.headerCol}>
                            <img src="/img/layout/map.svg" alt="" />
                        </div>
                        <div className={styles.headerCol}>
                            <div className={styles.headerText}>
                                {station.properties.address?.address_formatted}
                            </div>
                            <Distance
                                className={styles.headerDistance}
                                content={{ distance: station.distance }}
                            />
                        </div>
                    </div>
                    <a
                        className={styles.headerBtn}
                        href={`https://www.google.com/maps/dir/?api=1&travelmode=walking&destination=${station.geometry.coordinates[1]},${station.geometry.coordinates[0]}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <img src="/img/layout/location.svg" alt="" />
                        Navigovat
                    </a>
                </div>
                <div className={styles.infoBox}>
                    <section className={styles.infoRow}>
                        <h2 className={styles.infoLabel}>Oblast činnosti</h2>
                        <div className={styles.infoValue}>
                            {subject.skills || '–'}
                        </div>
                    </section>
                    <section className={styles.infoRow}>
                        <h2 className={styles.infoLabel}>Kontaktní údaje</h2>
                        <div className={styles.infoValue}>
                            {subject.phone && (
                                <div className={styles.infoContact}>
                                    <img
                                        src="/img/layout/circular-phone.svg"
                                        alt=""
                                    />
                                    <a
                                        href={`tel:${subject.phone.replace(
                                            /[^+\d]/g,
                                            ''
                                        )}`}
                                    >
                                        {subject.phone}
                                    </a>
                                </div>
                            )}
                            {subject.email && (
                                <div className={styles.infoContact}>
                                    <img
                                        src="/img/layout/circular-email.svg"
                                        alt=""
                                    />
                                    <a
                                        className={styles.infoLink}
                                        href={`mailto:${subject.email}`}
                                    >
                                        {subject.email}
                                    </a>
                                </div>
                            )}
                            {website && (
                                <div className={styles.infoContact}>
                                    <img
                                        src="/img/layout/circular-web.svg"
                                        alt=""
                                    />
                                    <a
                                        className={styles.infoLink}
                                        href={website}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {subject.website}
                                    </a>
                                </div>
                            )}
                            {!subject.phone &&
                                !subject.email &&
                                !website &&
                                '–'}
                        </div>
                    </section>
                    <section className={styles.infoRow}>
                        <h2 className={styles.infoLabel}>Otevírací doba</h2>
                        <div className={styles.infoValue}>
                            <div className={styles.infoContact}>
                                <img
                                    src="/img/layout/circular-clock.svg"
                                    alt=""
                                />
                                <span>{subject.openingHours || '–'}</span>
                            </div>
                        </div>
                    </section>
                    {updatedAt && (
                        <p className={styles.updatedAt}>
                            Poslední aktualizace dat:{' '}
                            <time dateTime={subject.updatedAt}>
                                {updatedAt}
                            </time>
                        </p>
                    )}
                </div>
            </>
        </ResultWrapper>
    )
}
